import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomCarousel from './CustomCarousel'; // Adjust the import path as needed
import AlertDialog from './AlertDialog';

function CartShowcase({ items, price, showCheckoutButton = true, onCheckout }) {
  const [total, totalPrice, discount] = price;
  const [expandedItemId, setExpandedItemId] = useState(null);
  const navigate = useNavigate();

  // Function to handle checkout navigation
  const handleCheckout = () => {
    // Check if cart is empty
    if (items.length === 0) {
      AlertDialog(
        "Empty Cart",
        "Please add items to your cart before proceeding to checkout.",
        "warning",
        2000,
        true,
        false,
        "OK",
        "",
        () => {}, 
        null
      );
      return;
    }

    // Check if all items have active plans selected
    const itemsWithoutPlans = items.filter(item => !item.activePlan);
    
    if (itemsWithoutPlans.length > 0) {
      AlertDialog(
        "Plan Selection Required",
        `Please select a plan for all items before proceeding to checkout.
        
        ${itemsWithoutPlans.length} item(s) still need plan selection.`,
        "warning",
        3000,
        true,
        false,
        "OK",
        "",
        () => {}, 
        null
      );
      return;
    }

    // If onCheckout function is provided (from parent component), use it
    if (onCheckout && typeof onCheckout === 'function') {
      onCheckout();
      return;
    }

    // Default checkout navigation
    AlertDialog(
      "Proceeding to Checkout",
      "Taking you to the checkout page...",
      "success",
      800,
      false,
      false,
      "",
      "",
      () => {}, 
      null
    );

    setTimeout(() => {
      navigate("/checkout", {
        state: {
          cartItems: items,
          discount,
          totalPrice,
          total,
        },
      });
    }, 1000);
  };

  // Function to continue shopping
  const continueShopping = () => {
    AlertDialog(
      "Continue Shopping",
      "Taking you back to browse more services...",
      "info",
      800,
      false,
      false,
      "",
      "",
      () => {}, 
      null
    );

    setTimeout(() => {
      navigate("/"); // Adjust this route as needed for your app
    }, 1000);
  };

  // Function to show item details in an alert
  const showItemDetails = (item) => {
    const planInfo = item.activePlan 
      ? `${item.activePlan.plan_name} - ${item.activePlan.plan_duration} - $${item.activePlan.price}`
      : "No plan selected";
    
    const vehicleDetails = `
    Vehicle Information:
    • Registration Number: ${item.carNumber || "N/A"}
    • Make: ${item.make || "N/A"}
    • Model: ${item.model || item.modelName || "N/A"}
    • Build Date: ${item.regDate || item.firstRegistrationDate || "N/A"}
    • Last Service Date: ${item.motExpDate || item.motExpiryDate || "N/A"}
    
    Service Information:
    • Type: ${item.carType || item.serviceType || "N/A"}
    • Engine: ${item.engineCapacity || "N/A"}
    • Selected Plan: ${planInfo}
    • Product Code: ${item.productCode || "N/A"}
    
    Booking Details:
    • Booking Date: ${item.bookingDate || "N/A"}
    • Postcode: ${item.postcode || "N/A"}
    
    Customer Information:
    • Name: ${item.fname || item.name || "N/A"} ${item.lname || ""}
    • Phone: ${item.phone || item.phoneNumber || "N/A"}
    • Email: ${item.email || "N/A"}
    `;
    
    AlertDialog(
      `${item.headTitle || item.modelName || "Service"} Details`,
      vehicleDetails,
      "info",
      0, // No auto-close
      true,
      false,
      "Close",
      "",
      () => {}, // Empty function to avoid 'not a function' error
      null
    );
  };

  // Function to show discount details
  const showDiscountInfo = () => {
    AlertDialog(
      "Discount Information",
      `Your order qualifies for a discount of $${discount}.
      
      This has been automatically applied to your total.`,
      "success",
      3000,
      true,
      false,
      "OK",
      "",
      () => {}, // Empty function to avoid 'not a function' error
      null
    );
  };

  // Function to confirm order details
  const confirmOrderDetails = () => {
    const itemsList = items.map((item, index) => 
      `${index + 1}. ${item.headTitle || item.modelName || "Service"} - ${item.activePlan ? `$${item.activePlan.price}` : "No plan selected"}`
    ).join('\n');

    AlertDialog(
      "Confirm Order Details",
      `Your order contains ${items.length} item(s):

${itemsList}

Subtotal: $${totalPrice}
Discount: -$${discount}
Total: $${total}

Please review all details before proceeding to payment.`,
      "warning",
      0, // No auto-close
      true,
      true,
      "Details are correct",
      "Review again",
      () => {
        // Show success confirmation
        AlertDialog(
          "Thank You",
          "Order details confirmed!",
          "success",
          1500,
          false,
          false,
          "",
          "",
          () => {}, // Empty function
          null
        );
      },
      () => {
        // User wants to review again - no action needed
        // The dialog will just close
      }
    );
  };

  // Function to toggle expanded view for an item
  const toggleExpanded = (itemId) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };

  return (
    <div>
      <h4 className="d-flex justify-content-between align-items-center mb-3">
        <span className="fs-4 text-muted">Your cart</span>
        <span className="badge bg-secondary rounded-pill">
          {items.length}
        </span>
      </h4>
      <ul className="list-group mb-3">
        {items.map((item, index) => (
          <li
            className="list-group-item d-flex flex-column lh-condensed"
            key={item.id || index}
          >
            {/* Main Item Display */}
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <h6 className="my-0 cursor-pointer text-primary" 
                    onClick={() => showItemDetails(item)} 
                    style={{ cursor: 'pointer' }}>
                  {item.headTitle || item.modelName || "Service"} 
                  <small className="text-muted ms-2">(click for full details)</small>
                </h6>
                <small className="text-muted">
                  {item.carType || item.serviceType} | {item.engineCapacity || "N/A"}
                </small>
                {item.productDescription && (
                  <p className="my-1 small">{item.productDescription}</p>
                )}
              </div>
              <button 
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={() => toggleExpanded(item.id || index)}
              >
                {expandedItemId === (item.id || index) ? 'Hide' : 'Show'} Details
              </button>
            </div>

            {/* Expanded Details */}
            {expandedItemId === (item.id || index) && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="text-primary">Vehicle Information</h6>
                    <p className="mb-1"><strong>Registration:</strong> {item.carNumber || "N/A"}</p>
                    <p className="mb-1"><strong>Make/Model:</strong> {item.make || "N/A"} {item.model || item.modelName || "N/A"}</p>
                    <p className="mb-1"><strong>Build Date:</strong> {item.regDate || item.firstRegistrationDate || "N/A"}</p>
                    <p className="mb-1"><strong>Last Service Date:</strong> {item.motExpDate || item.motExpiryDate || "N/A"}</p>
                    {item.currentKm && <p className="mb-1"><strong>Current KM:</strong> {item.currentKm}</p>}
                    {item.vinNumber && <p className="mb-1"><strong>VIN:</strong> {item.vinNumber}</p>}
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary">Booking Information</h6>
                    <p className="mb-1"><strong>Booking Date:</strong> {item.bookingDate || "N/A"}</p>
                    <p className="mb-1"><strong>Postcode:</strong> {item.postcode || "N/A"}</p>
                    <p className="mb-1"><strong>Product Code:</strong> {item.productCode || "N/A"}</p>
                    
                    {(item.fname || item.name) && (
                      <>
                        <h6 className="text-primary mt-3">Customer Information</h6>
                        <p className="mb-1"><strong>Name:</strong> {item.fname || item.name || "N/A"} {item.lname || ""}</p>
                        {item.phone && <p className="mb-1"><strong>Phone:</strong> {item.phone}</p>}
                        {item.email && <p className="mb-1"><strong>Email:</strong> {item.email}</p>}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Plan and Price Information */}
            <div className="mt-2 d-flex justify-content-between align-items-center">
              <div>
                <p className="mb-1">
                  <strong>Selected Plan:</strong>{' '}
                  {item.activePlan ? (
                    <span className="text-success">
                      {item.activePlan.plan_name} - {item.activePlan.plan_duration}
                    </span>
                  ) : (
                    <span className="text-warning">No plan selected</span>
                  )}
                </p>
                {item.serviceType && (
                  <p className="mb-1">
                    <strong>Service Type:</strong> {item.serviceType}
                  </p>
                )}
              </div>
              <div className="text-end">
                <strong className="fs-5">
                  ${item.activePlan ? item.activePlan.price : "0.00"}
                </strong>
              </div>
            </div>
          </li>
        ))}
        
        {/* Price Summary */}
        <li className="list-group-item d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <span>Subtotal (USD)</span>
            <span>${totalPrice}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span className="cursor-pointer text-success" 
                  onClick={showDiscountInfo} 
                  style={{ cursor: 'pointer' }}>
              Discount (USD) <i className="fas fa-info-circle"></i>
            </span>
            <strong className="text-success">-${discount}</strong>
          </div>
          <hr className="my-2" />
          <div className="d-flex justify-content-between align-items-center">
            <span className="h5">Total (USD)</span>
            <strong className="h5 text-primary">${total}</strong>
          </div>
          
          {/* Navigation Buttons */}
          {showCheckoutButton && (
            <div className="d-flex flex-column gap-2 mt-3">
              <button 
                className="btn btn-primary btn-lg w-100"
                onClick={handleCheckout}
                disabled={items.length === 0}
              >
                <i className="fas fa-credit-card me-2"></i>
                Proceed to Checkout
              </button>
              
              <button 
                className="btn btn-outline-secondary w-100"
                onClick={continueShopping}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Continue Shopping
              </button>
            </div>
          )}
          
          {/* Order confirmation button */}
          <button 
            className="btn btn-outline-primary mt-3 w-100"
            onClick={confirmOrderDetails}
          >
            <i className="fas fa-check-circle me-2"></i>
            Confirm Order Details
          </button>
        </li>
      </ul>
    </div>
  );
}

export default CartShowcase;